import { useDispatch } from 'react-redux';
import { ActionCreatorWithoutPayload } from '@reduxjs/toolkit';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useUpdateAccountMutation } from '@services/account/account';
import useIntl from '@i18n/useIntl';
import { Account } from '@models/account';
import { SnackbarTypes } from '@components/notifications/enums';
import { addNotification } from 'src/shared/state/notifications-slice';
import {
  ACCOUNT_SETTINGS_PAGE_ID,
  SUBMIT_FORM_ERROR_SNACKBAR_MESSAGE_KEY,
  SUBMIT_FORM_SUCCESS_SNACKBAR_MESSAGE_KEY,
} from 'src/pages/accountSettings/constants';
import usePerformance from 'src/shared/hooks/usePerformance';
import { getErrorMessageText } from '@features/account-registration/errors';
import get from 'lodash/get';
import { UpdateAccountResponse } from '@services/account/types';

const useSubmit = (callback: ActionCreatorWithoutPayload) => {
  const { intl } = useIntl();
  const { entityId } = useParams();
  const dispatch = useDispatch();
  const [updateAccount, { isLoading }] = useUpdateAccountMutation();
  const { start, end, cancel } = usePerformance({
    key: 'UpdateAccountTime',
  });
  const { reset } = useFormContext();

  const onSubmit = async (data: Partial<Account>) => {
    start();
    try {
      const { error }: UpdateAccountResponse = await updateAccount({
        entityId: entityId!,
        data,
      }).unwrap();

      const errorCode = get(error, [0, 'errors', 0, 'code'], null);

      if (errorCode) {
        // Multi-status with error
        cancel();
        const errorMessage = getErrorMessageText(intl, errorCode);
        dispatch(
          addNotification({
            id: ACCOUNT_SETTINGS_PAGE_ID,
            message: errorMessage,
            type: SnackbarTypes.Error,
          }),
        );
        return;
      }

      // Multi-status with success
      end();
      callback && dispatch(callback());
      dispatch(
        addNotification({
          id: ACCOUNT_SETTINGS_PAGE_ID,
          message: intl.getMessage(SUBMIT_FORM_SUCCESS_SNACKBAR_MESSAGE_KEY),
          type: SnackbarTypes.Success,
        }),
      );
    } catch (error) {
      cancel();
      dispatch(
        addNotification({
          id: ACCOUNT_SETTINGS_PAGE_ID,
          message: intl.getMessage(SUBMIT_FORM_ERROR_SNACKBAR_MESSAGE_KEY),
          type: SnackbarTypes.Error,
        }),
      );
    }
  };

  const onReset = () => {
    reset();
    callback && dispatch(callback());
  };

  return {
    onSubmit,
    onReset,
    isLoading,
  };
};

export default useSubmit;
